<template>
  <b-modal
    :id="id"
    :visible="isVisible"
    centered
    no-close-on-esc
    hide-header
    hide-footer
    no-fade
    modal-class="checkin-modal"
    @hidden="onClose"
  >
    <template #default>
      <div class="checkin-modal-content">
        <!-- Icon -->
        <div class="text-center mb-3">
          <div class="reward-icon">🎉</div>
          <h3 class="text-white mb-2">ยืนยันรับรางวัล</h3>
          <p class="reward-text">คุณจะได้รับเครดิตฟรี {{ reward }} บาท</p>
        </div>

        <!-- Success State -->
        <div v-if="claimed" class="success-animation">
          <div class="checkmark-circle">
            <div class="checkmark">✓</div>
          </div>
          <p class="success-text">รับรางวัลสำเร็จ!</p>
          <b-button
            variant="warning"
            block
            class="mt-3 go-dashboard-btn"
            @click="goToDashboard"
          >
            ไปหน้าหลัก
          </b-button>
        </div>

        <!-- Action Buttons -->
        <div v-if="!claimed" class="button-group">
          <b-button
            variant="outline-light"
            block
            @click="handleCancel"
            class="cancel-btn"
          >
            ยกเลิก
          </b-button>
          <b-button
            variant="success"
            block
            @click="handleConfirm"
            :disabled="loading"
            class="confirm-btn"
          >
            <b-spinner v-if="loading" small class="mr-1" />
            <span v-if="!loading">รับรางวัล</span>
            <span v-else>กำลังดำเนินการ...</span>
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BButton, BSpinner } from 'bootstrap-vue'

export default {
  name: 'CheckInModal',
  components: {
    BModal,
    BButton,
    BSpinner
  },
  props: {
    id: {
      type: String,
      default: 'checkin-modal'
    },
    value: {
      type: Boolean,
      default: false
    },
    reward: {
      type: Number,
      default: 100
    },
    success: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isVisible: false,
      loading: false,
      claimed: false
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.isVisible = newVal
        if (newVal && this.success) {
          // Open in success state
          this.claimed = true
        }
        if (!newVal) {
          // Reset state when modal closes
          setTimeout(() => {
            this.loading = false
            this.claimed = false
          }, 300)
        }
      },
      immediate: true
    }
  },
  methods: {
    handleCancel() {
      this.$emit('input', false)
      this.$emit('cancel')
    },
    async handleConfirm() {
      this.loading = true
      try {
        await this.$emit('confirm')
        this.claimed = true
        // Auto close after 2 seconds
        setTimeout(() => {
          this.$emit('input', false)
        }, 2000)
      } catch (error) {
        this.loading = false
      }
    },
    onClose() {
      this.$emit('input', false)
    },
    goToDashboard() {
      this.$emit('go-dashboard')
      this.$emit('input', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.checkin-modal-content {
  padding: 24px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;

  @media (min-width: 768px) {
    padding: 32px;
  }
}

.reward-icon {
  font-size: 64px;
  margin-bottom: 16px;
  animation: bounce 0.6s ease;

  @media (min-width: 768px) {
    font-size: 72px;
  }
}

h3 {
  font-size: 20px;
  font-weight: bold;
  color: #ffcc00;

  @media (min-width: 768px) {
    font-size: 24px;
  }
}

.reward-text {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
}

.button-group {
  display: flex;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
}

.cancel-btn,
.confirm-btn {
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 10px;
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    font-size: 16px;
  }
}

.cancel-btn {
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
}

.confirm-btn {
  background: linear-gradient(135deg, #28a745, #20c997);
  border: none;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(40, 167, 69, 0.5);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

// Success animation
.success-animation {
  text-align: center;
  padding: 20px 0;
  animation: fadeIn 0.4s ease;
}

.checkmark-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28a745, #20c997);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  animation: scale-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @media (min-width: 768px) {
    width: 96px;
    height: 96px;
  }
}

.checkmark {
  font-size: 48px;
  color: #ffffff;
  font-weight: bold;

  @media (min-width: 768px) {
    font-size: 56px;
  }
}

.success-text {
  font-size: 18px;
  font-weight: bold;
  color: #28a745;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 20px;
  }
}

// Animations
@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scale-in {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>

<style lang="scss">
// Global modal styles
.checkin-modal {
  background-color: rgba(0, 0, 0, 0.9) !important;

  .modal-dialog {
    background: transparent;
  }

  .modal-content {
    background: transparent;
    border: none;
  }

  .modal-body {
    padding: 0;
    background: transparent;
  }

  & + .modal-backdrop {
    display: none !important;
  }
}
</style>
